import React, { useState, DragEvent, ChangeEvent } from 'react';
import { HiOutlineCloudUpload } from 'react-icons/hi';

/* ------------------------------ ImageUploader ------------------------------ */

type ImageUploaderProps = {
  onUpload?: (file: File) => void;
  label?: string;
  className?: string;
  initialUrl?: string;
};

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUpload,
  label = 'Upload Image',
  className = '',
  initialUrl,
}) => {
  const [preview, setPreview] = useState<string | null>(initialUrl || null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setPreview(URL.createObjectURL(file));
    onUpload?.(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className={`w-full ${className}`}>
      {label && <p className='mb-2 text-sm font-medium text-gray-700 dark:text-gray-200'>{label}</p>}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`
          relative border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center transition-all duration-300 cursor-pointer overflow-hidden min-h-[176px]
          ${isDragging ? 
            'border-primary-500 bg-primary-50 dark:bg-primary-900/10' 
            : 'border-gray-300 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 bg-gray-50 hover:bg-white dark:bg-gray-900/50 dark:hover:bg-gray-900/80 shadow-sm hover:shadow-md'
          }
        `}
        onClick={() => document.getElementById('imageInput')?.click()}
      >
        {preview ? (
          <div className='relative w-full h-full group'>
             <img
              src={preview}
              alt='Preview'
              className='w-full h-44 object-cover rounded-lg'
            />
            <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg'>
              <p className='text-white font-medium'>Change Image</p>
            </div>
          </div>
        ) : (
          <div className='flex flex-col items-center py-4'>
            <div className='w-12 h-12 bg-white dark:bg-gray-700 rounded-full shadow-sm flex items-center justify-center mb-3 text-primary-500'>
              <HiOutlineCloudUpload className='w-6 h-6' />
            </div>
            <p className='text-gray-700 dark:text-gray-200 font-medium text-sm'>Click to upload</p>
            <p className='text-gray-400 dark:text-gray-500 text-xs mt-1'>or drag and drop here</p>
          </div>
        )}
        <input
          id='imageInput'
          type='file'
          accept='image/*'
          onChange={handleChange}
          className='hidden'
        />
      </div>
    </div>
  );
};

export default ImageUploader;

/* ---------------------------- Example Usage ----------------------------

<ImageUploader onUpload={(file) => console.log(file)} label="Profile Picture" />

*/
